import { Button, Col, Drawer, Grid, Image, InputNumber, Row } from "antd";
import "./OrderList.scss";
import { useEffect, useMemo, useState } from "react";
import PaymentConfirm from "../PaymentConfirm/PaymentConfirm";
import { isEmpty } from "lodash";
import useModal from "../../../../../hooks/useModal";
import usePageState from "../../../../../hooks/usePageState";
import useAsyncEffect from "../../../../../hooks/useAsyncEffect";
import {
  splitNote,
  handleSugarIceChange,
  handleCustomChange,
} from "../../../../../../applications/useCase/BusinessUseCase/ConvertNoteUseCase";
import { currencyFormat } from "../../../../../../applications/utils/NumberUtils";
import {
  LIMIT_PER_PAGE,
  SIZE_MAPPING_OPTION,
  ITEM_TYPE_ENUM,
  SUGAR_ICE_OPTION,
  SALE_VALUE_TYPE_ENUM,
  SALE_TYPE_ENUM,
} from "../../../../../../applications/constants/AppConst";
import { SaleInfoResponse } from "../../../../../../infrastructure/model/SaleInfoModel";
import AppDropdown from "../../../../../components/AppDropdown";
import AppInputDropdownMultiple from "../../../../../components/AppDropdownMultiple";
import AppInputText from "../../../../../components/AppInputText/AppInputText";
import Visible from "../../../../../components/Visible";
import { useItemsContext } from "../../../../../context/AppItemsContext";
import { useToppingContext } from "../../../../../context/AppToppingContext";
import { useOrderList } from "../../../../../context/OrderListContext";
import Column from "../../../../../components/Column";
import {
  calculateOrderPrice,
  OrderCalculationResult,
} from "../../../../../../applications/useCase/BusinessUseCase/CalculateOrderPriceUseCase";
import { useAuth } from "../../../../../context/AuthContext";
import { getAllSaleInfoUseCase } from "../../../../../../applications/useCase/ApiUseCase/SaleApiUseCase";

const { useBreakpoint } = Grid;

interface OrderListProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

function OrderList(props: OrderListProps) {
  const { open, setOpen } = props;
  const { authData } = useAuth();
  const { items } = useItemsContext();
  const { orderList, updateOrder, removeFromOrder } = useOrderList();
  const { toppings } = useToppingContext();
  const [drawerWidth, setDrawerWidth] = useState<number | string>(350);
  const { showError } = usePageState();
  const [saleActive, setSaleActive] = useState<SaleInfoResponse[]>([]);
  const [customNoteMap, setCustomNoteMap] = useState<Map<number, string>>(new Map());

  const toppingCanAddMore = useMemo(() => {
    return toppings?.filter((e) => e.can_sale);
  }, [toppings]);

  async function getAllSaleInfo() {
    const res = await getAllSaleInfoUseCase({
      page: 1,
      limit: LIMIT_PER_PAGE,
      is_sale_order: undefined,
      is_active: true,
      showError,
    });
    if (res) {
      setSaleActive(res.data);
    }
  }

  const screens = useBreakpoint();
  const [paymentModal, openPaymentModal] = useModal();

  useEffect(() => {
    // Ví dụ: Nếu màn hình nhỏ hơn md, thì dùng full width
    setDrawerWidth(screens.md ? 350 : "100%");
  }, [screens]);

  const handlePaymentConfirm = () => {
    handleOpenPaymentDialog();
    setOpen(false);
  };

  useAsyncEffect(async () => {
    if (open) {
      await getAllSaleInfo();
    }
  }, [open]);

  const handleCalculateOrderPrice: OrderCalculationResult = useMemo(() => {
    if (!orderList) return {} as OrderCalculationResult;
    return calculateOrderPrice(orderList, saleActive, {
      useNewUserSale: true,
      totalOrders: authData?.total_orders ?? 0,
    });
  }, [saleActive, orderList]);

  // Lọc khuyến mãi không trùng lặp theo id
  const bestSale = useMemo(() => {
    return handleCalculateOrderPrice?.bestSale?.sale
      ? [handleCalculateOrderPrice?.bestSale.sale]
      : [];
  }, [handleCalculateOrderPrice.bestSale]);

  async function handleOpenPaymentDialog() {
    const result = await openPaymentModal((close) => {
      return (
        <PaymentConfirm
          onClose={close}
          calculateOrderPrice={handleCalculateOrderPrice}
          bestSale={bestSale}
          saleActives={saleActive}
          toppingCanAddMore={toppingCanAddMore}
        />
      );
    });
  }

  const getSizeMappingByItem = (itemId: number) => {
    return items?.find((i) => i.id === itemId)?.size_mapping || [];
  };

  const getSizeMappingOptionByItem = (itemId: number) => {
    const itemSizeMapping = getSizeMappingByItem(itemId)?.map((s) => s.size) || [];
    return SIZE_MAPPING_OPTION?.filter((op) => itemSizeMapping?.includes(op.value));
  };

  return (
    <>
      <Drawer
        className="order-shopping-wrapper"
        title="Giỏ hàng"
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        width={drawerWidth}
      >
        <div className="order-items" style={{ width: screens.md ? 350 : "100%" }}>
          {orderList?.length === 0 ? (
            <div>Giỏ hàng trống</div>
          ) : (
            <div className="datas">
              {handleCalculateOrderPrice?.items.map((order, index) => {
                const noteOptions = [ITEM_TYPE_ENUM.COFFEE_MATCHA_HOT_DRINK]?.includes(order.type)
                  ? SUGAR_ICE_OPTION
                  : SUGAR_ICE_OPTION?.filter((_, i) => i !== 0);
                const { sugarIce, custom } = splitNote(order?.note || "");
                return (
                  <Column key={index}>
                    <Row className="w-100 order-item-pendding mb-2 gap-2 flex">
                      <Col span={6} className="order-item-image">
                        <Image src={order.image} alt={order.name} width={"100%"} />
                      </Col>
                      <Col span={17} className="order-item-info">
                        <div style={{ fontWeight: 600 }}>{order.name}</div>
                        <Visible
                          isVisible={[
                            ITEM_TYPE_ENUM.BLACK_MILK_TEA,
                            ITEM_TYPE_ENUM.OOLONG_MILK_TEA,
                            ITEM_TYPE_ENUM.FRESH_FRUIT_JUICE,
                            ITEM_TYPE_ENUM.NON_CAFFEIN,
                          ].includes(order.type)}
                        >
                          <AppInputDropdownMultiple
                            options={toppingCanAddMore?.map((e) => {
                              return {
                                label: e.name + " + " + currencyFormat(e.price),
                                value: e.id,
                              };
                            })}
                            className="w-100 p-1"
                            placeholder="Thêm topping"
                            currentValue={order.moreTopping?.map((e) => e.id)}
                            onChangeValue={(newToppingIds: number[]) => {
                              updateOrder({
                                ...order,
                                moreTopping: toppingCanAddMore?.filter((t) =>
                                  newToppingIds?.includes(t.id)
                                ),
                              });
                            }}
                          />
                        </Visible>

                        <Row className="mt-2 gap-2 pl-1 pr-1 justify-content-between">
                          <Col span={7} className="flex flex-column gap-2 justify-content-end">
                            <div>SL</div>
                            <InputNumber
                              className="w-100"
                              min={1}
                              value={order.quantity}
                              onChange={(e) =>
                                updateOrder({
                                  ...order,
                                  quantity: e!,
                                })
                              }
                            />
                          </Col>
                          <Col span={7} className="flex flex-column gap-2 justify-content-end">
                            <div>Size</div>
                            <AppDropdown
                              options={getSizeMappingOptionByItem(order.id)}
                              currentValue={order.size}
                              onChangeValue={(e) =>
                                updateOrder({
                                  ...order,
                                  size: e,
                                  price:
                                    getSizeMappingByItem(order.id)?.find((op) => op.size === e)
                                      ?.price || 0,
                                })
                              }
                            />
                          </Col>
                          <Col span={7} className="gap-2 flex flex-column justify-content-end">
                            <div>
                              {(() => {
                                const toppingTotal =
                                  order.moreTopping?.reduce(
                                    (sum, topping) =>
                                      topping.can_sale ? sum + topping.price : sum,
                                    0
                                  ) ?? 0;
                                const basePrice = (order.price + toppingTotal) * order.quantity;
                                const finalPrice = order.finalPrice * order.quantity;

                                return order.appliedSale && basePrice !== finalPrice ? (
                                  <>
                                    <div style={{ color: "#888", textDecoration: "line-through" }}>
                                      {currencyFormat(basePrice)}
                                    </div>
                                    <div style={{ color: "#dd4f4f", fontWeight: 600 }}>
                                      {currencyFormat(finalPrice)}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ color: "#dd4f4f", fontWeight: 600 }}>
                                    {currencyFormat(finalPrice)}
                                  </div>
                                );
                              })()}
                            </div>

                            <Button
                              style={{ width: "100%" }}
                              color="danger"
                              variant="outlined"
                              onClick={() => removeFromOrder(order.indexAdd!)}
                            >
                              Xóa
                            </Button>
                          </Col>
                        </Row>
                        <div className="w-100 p-1">
                          <AppInputDropdownMultiple
                            currentValue={sugarIce}
                            maxCount={2}
                            onChangeValue={(e: string[]) => {
                              handleSugarIceChange(order, e, updateOrder);
                            }}
                            placeholder="Đường, đá..."
                            options={noteOptions}
                          />
                        </div>
                        <div className="p-1 ">
                          <AppInputText
                            currentValue={customNoteMap.get(order.indexAdd!) || ""}
                            onTextChange={(e: string) => {
                              handleCustomChange(
                                order,
                                e,
                                updateOrder,
                                customNoteMap,
                                setCustomNoteMap
                              );
                            }}
                            additionProps={{
                              placeholder: "Ghi chú khác",
                              maxLength: 30,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Column>
                );
              })}
            </div>
          )}
        </div>

        {/* Tổng tiền và khuyến mãi */}
        <div className="payment" style={{ visibility: isEmpty(orderList) ? "hidden" : "visible" }}>
          <Row className="flex flex-row w-100">
            <Col span={12} style={{ fontWeight: 600 }} className="flex pl-4">
              Tổng tiền gốc:
            </Col>
            <Col span={12} className="flex justify-content-end pr-4">
              {currencyFormat(handleCalculateOrderPrice?.originalTotal)}
            </Col>
          </Row>

          {handleCalculateOrderPrice?.originalTotal !==
            handleCalculateOrderPrice?.discountedTotal && (
            <Row className="flex flex-row w-100 mt-1">
              <Col span={12} style={{ fontWeight: 600 }} className="flex pl-4">
                Sau khuyến mãi:
              </Col>
              <Col span={12} className="flex justify-content-end pr-4" style={{ color: "#dd4f4f" }}>
                {currencyFormat(handleCalculateOrderPrice?.discountedTotal)}
              </Col>
            </Row>
          )}

          {/* ✅ Hiển thị khuyến mãi đã áp dụng (cả đơn và sản phẩm) */}
          {bestSale?.length > 0 && (
            <div className="mt-2 " style={{ fontSize: 12, color: "#4096ff" }}>
              <div className="pl-4">
                <b>Khuyến mãi đã áp dụng:</b>
              </div>
              <ul className="mt-1">
                {bestSale?.map((sale) => {
                  const numberFreeCup =
                    handleCalculateOrderPrice.appliedAllSales?.filter((s) => s.id === sale.id)
                      ?.length || 0;
                  return (
                    <li key={sale.id}>
                      🎁 <b>{sale.title}</b> (
                      {sale.sale_value_type === SALE_VALUE_TYPE_ENUM.CUP
                        ? (sale?.sale_value || 1) * numberFreeCup
                        : sale.sale_value}
                      {sale.sale_value_type === SALE_VALUE_TYPE_ENUM.PERCENT
                        ? " %"
                        : sale.sale_value_type === SALE_VALUE_TYPE_ENUM.VND
                        ? " đ"
                        : " cốc"}{" "}
                      –
                      {sale.sale_type === SALE_TYPE_ENUM.ALL_BILL
                        ? "Toàn đơn"
                        : sale.sale_type === SALE_TYPE_ENUM.EACH_ITEM
                        ? "Sản phẩm"
                        : "Combo"}
                      )
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <Button
            className="w-100 mt-3"
            style={{ height: "40px" }}
            onClick={handlePaymentConfirm}
            color="danger"
            variant="solid"
          >
            Thanh toán
          </Button>
        </div>
      </Drawer>
      {paymentModal}
    </>
  );
}

export default OrderList;
